import React from 'react';
import FilterableContent from 'common/templates/FilterableContent';
import GroupedCardContent from 'common/template-parts/GroupedCardContent';
import CustomizableCard from 'common/template-parts/CustomizableCard';
import AuthSequencingCardFooter from 'feature/loading-management/authorization-sequencing/templates/AuthSequencingCardFooter';
import AuthSequencingFilterContent from 'feature/loading-management/authorization-sequencing/templates/AuthSequencingFilterContent';
import AuthSequencingMenu from 'feature/loading-management/authorization-sequencing/templates/AuthSequencingMenu';
import AuthorizationSequencingCardContent from 'feature/loading-management/authorization-sequencing/templates/AuthorizationSequencingCardContent';
import LoadableChip from 'feature/loading-management/authorization-sequencing/templates/LoadableChip';
import { useAuthSequencingFilters, useCardChangeTracking } from 'hooks';
import { mockAuthSequences } from './mockData/authSequencingData';

export default function AuthSequencingPreview() {
  const {
    appliedFilters,
    draftFilters,
    handleChange,
    setSearch,
    handleApply,
    handleReset,
    hasChanges,
    isOnDefaultState,
  } = useAuthSequencingFilters();
  const { cardRefs, getCardRef, handleCardChange, getCardHasChanges } = useCardChangeTracking();

  // For preview purposes, apply client-side filtering to mock data
  // In production, this would come from the server
  const filteredData = React.useMemo(() => {
    let filtered = [...mockAuthSequences];

    if (appliedFilters.location) {
      filtered = filtered.filter(item => item.nameLong.includes(appliedFilters.location));
    }
    if (appliedFilters.product) {
      filtered = filtered.filter(item => item.product.nameLong === appliedFilters.product);
    }
    if (appliedFilters.status) {
      if (appliedFilters.status === 'Loadable') {
        filtered = filtered.filter(item => item.loadingAllowed);
      } else if (appliedFilters.status === 'Not Loadable') {
        filtered = filtered.filter(item => !item.loadingAllowed);
      }
    }

    if (appliedFilters.search) {
      const searchLower = appliedFilters.search.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.nameLong.toLowerCase().includes(searchLower) ||
          item.product.nameLong.toLowerCase().includes(searchLower) ||
          item.sequenced.some(auth => auth.inventoryOwner.toLowerCase().includes(searchLower)) ||
          item.notSequenced.some(auth => auth.inventoryOwner.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [appliedFilters]);

  const canEdit = true; // This would typically come from props or context to determine if the user has edit permissions

  // Group data by location for grouped card content
  const groupedByLocation = filteredData.reduce((acc, authData) => {
    const locationKey = authData.nameLong;
    if (!acc[locationKey]) {
      acc[locationKey] = [];
    }
    acc[locationKey].push(authData);
    return acc;
  }, {});

  return (
    <FilterableContent
      canToggleFilterDrawer // this can be turned off with canToggleFilterDrawer={false}
      filterContent={
        <AuthSequencingFilterContent filters={draftFilters} handleChange={handleChange} />
      }
      handleReset={handleReset}
      hasChanges={hasChanges}
      headerTitle="Authorization Sequencing Preview" // optional, null value will hide the header
      isOnDefaultState={isOnDefaultState}
      onApply={handleApply}
      onSearchChange={setSearch}
      primaryFilterActionButtonLabel="Apply"
      searchPlaceholder="Search authorizations..."
      searchValue={draftFilters.search}
      secondaryFilterActionButtonLabel="Reset"
      showQuickSearch // this can be turned off with showQuickSearch={false}
      titleMenu={<AuthSequencingMenu />}
    >
      <GroupedCardContent
        getItemKey={authData => `${authData.locationId}-${authData.productId}`}
        groupedData={groupedByLocation}
        renderCard={authData => (
          <CustomizableCard
            cardContent={
              <AuthorizationSequencingCardContent
                authSequenceData={authData}
                canEdit={canEdit}
                onAuthSequenceChange={() =>
                  handleCardChange(authData.locationId, authData.productId, true)
                }
                onFutureAuthSequenceClick={() => {
                  // eslint-disable-next-line no-console
                  console.log(
                    `Future auth sequence clicked for location ${authData.locationId}, product ${authData.productId}`
                  );
                }}
                ref={getCardRef(authData.locationId, authData.productId)}
              />
            }
            cardFooter={
              <AuthSequencingCardFooter
                cardRefs={cardRefs}
                getCardHasChanges={getCardHasChanges}
                handleCardChange={handleCardChange}
                locationId={authData.locationId}
                productId={authData.productId}
              />
            }
            cardHeader={authData.nameLong}
            cardHeaderRightSideContent={<LoadableChip loadingAllowed={authData.loadingAllowed} />}
            // Using testStatus to demonstrate different card statuses in the preview
            status={authData.testStatus || null}
          />
        )}
      />
    </FilterableContent>
  );
}
